// Drives the "make your own bundle" block rendered by snippets/shade-bundle-builder.liquid.
//
// Each shade in the bundle is a standalone product, so "add bundle to cart" posts one cart line
// per selected shade rather than a single line with a quantity.
//
// The cart belongs to Shopflo, whose bridge script (layout/theme.liquid) replaces the native cart
// and checkout. Like the theme's own add-to-cart, this never navigates: it adds over AJAX and then
// asks Shopflo to open its drawer via window.handleFloCartBtn. Navigating to /cart instead would
// land the shopper on Shopify's native cart, outside the Shopflo flow. The theme's own drawer is
// left alone — refreshing it by hand is what had to be disabled for Shopflo in gift-auto-add.js.
// A bundle in progress outlives both the section swaps product-info.js performs on every shade
// change and a real page load, so it's held in localStorage and mirrored in memory. Only tiers
// the shopper has actually edited get written — an untouched tier keeps following whatever
// defaults the merchant has set, rather than freezing the ones in place on their first visit.
const BUNDLE_STORAGE_PREFIX = 'promore:shade-bundle:';
const bundleStates = new Map();

function readBundleState(key) {
  if (bundleStates.has(key)) return bundleStates.get(key);

  let stored = null;
  try {
    stored = JSON.parse(localStorage.getItem(BUNDLE_STORAGE_PREFIX + key) || 'null');
  } catch (e) {
    // Unavailable in private mode or with site data blocked — fall through to a blank state.
  }

  const state = {
    activeQuantity: stored && stored.activeQuantity ? stored.activeQuantity : null,
    tiers: stored && stored.tiers && typeof stored.tiers === 'object' ? stored.tiers : {},
  };

  bundleStates.set(key, state);
  return state;
}

function writeBundleState(key, state) {
  bundleStates.set(key, state);
  try {
    localStorage.setItem(BUNDLE_STORAGE_PREFIX + key, JSON.stringify(state));
  } catch (e) {
    // Storage full or blocked: the in-memory copy still carries the shopper through this page.
  }
}

if (!customElements.get('shade-bundle-builder')) {
  customElements.define(
    'shade-bundle-builder',
    class ShadeBundleBuilder extends HTMLElement {
      connectedCallback() {
        this.tabs = Array.from(this.querySelectorAll('[data-tier-tab]'));
        this.options = Array.from(this.querySelectorAll('[data-shade-option]'));
        this.pills = this.querySelector('[data-bundle-pills]');
        this.headingEl = this.querySelector('[data-bundle-heading]');
        this.hintEl = this.querySelector('[data-bundle-hint]');
        this.pricesEl = this.querySelector('[data-bundle-prices]');
        this.compareEl = this.querySelector('[data-price-compare]');
        this.finalEl = this.querySelector('[data-price-final]');
        this.saveEl = this.querySelector('[data-price-save]');
        this.openButton = this.querySelector('[data-open-picker]');
        this.addButton = this.querySelector('[data-add-bundle]');
        this.errorEl = this.querySelector('[data-bundle-error]');
        this.modal = this.querySelector('[data-picker-modal]');
        this.modalCount = this.querySelector('[data-picker-count]');
        this.fixedEl = this.querySelector('[data-fixed-shade]');

        if (!this.tabs.length || !this.options.length) return;

        // Each tab keeps its own selection, pre-filled from the tier's default shades, so
        // switching tabs never silently drops what the shopper already picked in the other one.
        this.tierSelections = new Map();
        this.selected = [];
        this.submitting = false;
        this.lastFocused = null;
        this.onKeydown = this.onKeydown.bind(this);
        this.onPageShow = this.onPageShow.bind(this);
        window.addEventListener('pageshow', this.onPageShow);

        // scrollTabIntoView only on a click: selectTier also runs on load and on every section
        // swap, where sliding the row would look like the page moving on its own.
        this.tabs.forEach((tab) =>
          tab.addEventListener('click', () => {
            this.selectTier(tab);
            this.scrollTabIntoView(tab);
          })
        );
        // Tapping a card adds one of that shade — the same shade can be tapped again for a
        // second unit, as long as there's a slot left. Removal happens from the pill instead.
        this.options.forEach((option) => option.addEventListener('click', () => this.addShade(option)));
        this.querySelectorAll('[data-picker-close]').forEach((el) =>
          el.addEventListener('click', () => this.closePicker())
        );
        this.openButton.addEventListener('click', () => this.openPicker());
        this.addButton.addEventListener('click', () => this.addBundleToCart());

        // The picker is position: fixed, but .product__info-wrapper carries a transform from the
        // theme's reveal-on-scroll animation, and a transformed element becomes the containing
        // block for fixed descendants. Left in place the modal fills that column instead of the
        // viewport, clipping most of the shades out of view — so it's moved to the body, outside
        // any transform. Every reference above is cached first, so they survive the move.
        document.body.appendChild(this.modal);

        this.state = readBundleState(this.dataset.storageKey || 'default');

        // Every visit opens on the first tier tab, whichever tier was used last time.
        this.selectTier(this.tabs[0]);
      }

      disconnectedCallback() {
        // The theme editor tears this element down on every settings change; leaving the modal's
        // key handler and the body scroll lock behind would break the rest of the page. Only
        // release the scroll lock if this element is the one holding it — the cart drawer uses
        // the same class.
        document.removeEventListener('keydown', this.onKeydown);
        window.removeEventListener('pageshow', this.onPageShow);
        if (this.modal && !this.modal.hidden) document.body.classList.remove('overflow-hidden');

        // The modal lives on the body rather than inside this element, so it has to be taken
        // down by hand — the section swap on a shade change would otherwise leave one behind
        // every time, each with its own listeners.
        if (this.modal && this.modal.parentElement === document.body) this.modal.remove();
      }

      // Coming back to a cached page (the browser's back button after a successful add) restores
      // the DOM exactly as it was left — mid-submit, with the button disabled and spinning.
      onPageShow() {
        if (!this.submitting) return;
        this.submitting = false;
        this.addButton.classList.remove('loading');
        this.render();
      }

      get required() {
        return this.activeTab ? parseInt(this.activeTab.dataset.quantity, 10) : 0;
      }

      get discount() {
        // Tier discounts are entered in the editor as whole currency units, not cents.
        return this.activeTab ? Math.round(parseFloat(this.activeTab.dataset.discount || 0) * 100) : 0;
      }

      selectTier(tab) {
        if (!tab) return;

        this.activeTab = tab;
        this.tabs.forEach((item) => {
          const isActive = item === tab;
          item.classList.toggle('is-active', isActive);
          item.setAttribute('aria-selected', isActive ? 'true' : 'false');
        });

        if (!this.tierSelections.has(tab)) {
          this.tierSelections.set(tab, this.buildSelection(tab));
        }
        this.selected = this.tierSelections.get(tab);

        this.clearError();
        this.render();
        this.persist(false);
      }

      // On mobile the tabs are a horizontal scroller, so a tab sitting past the right edge stays
      // half-hidden after being tapped unless the row is moved.
      scrollTabIntoView(tab) {
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        tab.scrollIntoView({
          // 'nearest' on both axes: horizontally it moves only as far as it must (so tapping an
          // already-visible tab does nothing), and vertically it stays put — the default would
          // scroll the whole product page to align the tab with the top of the viewport.
          inline: 'nearest',
          block: 'nearest',
          behavior: reduceMotion ? 'auto' : 'smooth',
        });
      }

      // A tier starts empty unless the shopper already picked shades at that size on a
      // previous visit — tiers are never pre-filled.
      buildSelection(tab) {
        const quantity = parseInt(tab.dataset.quantity, 10) || 0;
        const saved = this.state.tiers[String(quantity)];

        if (!Array.isArray(saved)) return [];

        return this.resolveShades(saved, quantity);
      }

      // Resolves ids against the picker options, so name, price and image only ever exist in one
      // place — and an id with no matching option (a shade now sold out) is simply dropped.
      resolveShades(ids, quantity) {
        return ids
          .filter(Boolean)
          .map((variantId) => this.options.find((option) => option.dataset.variantId === variantId))
          .filter(Boolean)
          .slice(0, quantity)
          .map((option) => this.readShade(option));
      }

      // touchTier marks the tier as shopper-edited; a plain tab switch only records which tier
      // they're looking at, so tiers they never touch keep tracking the merchant's defaults.
      persist(touchTier) {
        this.state.activeQuantity = this.required;
        if (touchTier) {
          this.state.tiers[String(this.required)] = this.selected.map((shade) => shade.variantId);
        }
        writeBundleState(this.dataset.storageKey || 'default', this.state);
      }

      readShade(option) {
        return {
          variantId: option.dataset.variantId,
          name: option.dataset.name,
          price: parseInt(option.dataset.price, 10) || 0,
          image: option.querySelector('img')?.currentSrc || option.querySelector('img')?.src || '',
        };
      }

      // The same shade can be added as many times as there are slots left — someone buying a
      // bundle of three may well want two of one colour.
      addShade(option) {
        if (this.selected.length >= this.required) return;

        this.selected.push(this.readShade(option));

        this.clearError();
        this.render();
        this.persist(true);

        // Stay open while there are slots left so a 7-shade bundle isn't seven round trips.
        if (this.selected.length >= this.required) this.closePicker();
      }

      removeShade(index) {
        this.selected.splice(index, 1);
        this.clearError();
        this.render();
        this.persist(true);
      }

      // Disables every card once the active tier is full, so a shopper can't tap a shade into
      // a slot that doesn't exist.
      renderOptionRows() {
        const required = this.required;
        this.options.forEach((option) => {
          option.disabled = this.selected.length >= required;
        });
      }

      // Before any tab is picked there's no tier to size the panel against, so it shows just
      // the always-included product (if any) with everything else inert.
      renderEmpty() {
        this.headingEl.textContent = '';
        this.pills.innerHTML = '';
        if (this.fixedEl) this.pills.appendChild(this.buildPill(this.readShade(this.fixedEl), null));
        this.hintEl.textContent = '';
        this.pricesEl.hidden = true;
        this.openButton.disabled = true;
        this.addButton.disabled = true;

        const addLabelEl = this.addButton.querySelector('.shade-bundle__button-label');
        if (addLabelEl) {
          addLabelEl.textContent = (this.dataset.addLabel || 'Add [count] to cart').replace('[count]', '0');
        }

        if (this.modalCount) this.modalCount.textContent = '';
      }

      render() {
        if (!this.activeTab) {
          this.renderEmpty();
          return;
        }

        const required = this.required;
        const remaining = required - this.selected.length;

        // The tier's own heading wins; the block-level one is the fallback for tiers left blank.
        const heading = this.activeTab.dataset.heading || this.dataset.heading || '';
        this.headingEl.textContent = heading.replace('[count]', required);

        this.pills.innerHTML = '';
        // The always-included product leads the row and carries no remove button.
        if (this.fixedEl) this.pills.appendChild(this.buildPill(this.readShade(this.fixedEl), null));

        this.selected.forEach((shade, index) => {
          this.pills.appendChild(this.buildPill(shade, () => this.removeShade(index)));
        });

        // One placeholder per empty slot, so the row always shows the full tier and it's obvious
        // how many shades are still to choose.
        for (let slot = 0; slot < Math.max(remaining, 0); slot += 1) {
          this.pills.appendChild(this.buildPlaceholder());
        }

        this.hintEl.textContent =
          remaining > 0 ? `Add ${remaining} more ${remaining === 1 ? 'shade' : 'shades'}` : '';

        this.renderPrices();
        this.renderOptionRows();

        this.openButton.disabled = this.selected.length >= required;
        this.addButton.disabled = this.selected.length !== required || this.submitting;

        const addLabelEl = this.addButton.querySelector('.shade-bundle__button-label');
        if (addLabelEl) {
          addLabelEl.textContent = (this.dataset.addLabel || 'Add [count] to cart').replace(
            '[count]',
            this.selected.length
          );
        }

        if (this.modalCount) {
          this.modalCount.textContent = `${this.selected.length} of ${required} selected`;
        }
      }

      // onRemove of null builds a locked pill — used for the always-included product.
      buildPill(shade, onRemove) {
        const pill = document.createElement('div');
        pill.className = onRemove ? 'shade-bundle__pill' : 'shade-bundle__pill shade-bundle__pill--locked';

        if (shade.image) {
          const img = document.createElement('img');
          img.src = shade.image;
          img.alt = shade.name;
          img.loading = 'lazy';
          pill.appendChild(img);
        }

        const name = document.createElement('span');
        name.className = 'shade-bundle__pill-name';
        name.textContent = shade.name;
        pill.appendChild(name);

        if (onRemove) {
          const remove = document.createElement('button');
          remove.type = 'button';
          remove.className = 'shade-bundle__pill-remove';
          remove.setAttribute('aria-label', `Remove ${shade.name}`);
          remove.textContent = '×';
          remove.addEventListener('click', onRemove);
          pill.appendChild(remove);
        }

        return pill;
      }

      // An empty slot doubles as a second way into the picker.
      buildPlaceholder() {
        const label = this.dataset.emptyLabel || 'Add Shade';

        const pill = document.createElement('button');
        pill.type = 'button';
        pill.className = 'shade-bundle__pill shade-bundle__pill--empty';
        pill.addEventListener('click', () => this.openPicker());

        const icon = document.createElement('span');
        icon.className = 'shade-bundle__pill-placeholder';
        icon.setAttribute('aria-hidden', 'true');
        icon.textContent = '?';
        pill.appendChild(icon);

        const name = document.createElement('span');
        name.className = 'shade-bundle__pill-name';
        name.textContent = label;
        pill.appendChild(name);

        return pill;
      }

      renderPrices() {
        // With every shade removed the always-included product is still in the bundle, so the
        // price stays on show — just its own. Nothing at all to price is the only empty case.
        if (!this.selected.length && !this.fixedEl) {
          this.pricesEl.hidden = true;
          return;
        }

        const fixedPrice = this.fixedEl ? this.readShade(this.fixedEl).price : 0;
        const total = this.selected.reduce((sum, shade) => sum + shade.price, fixedPrice);
        // A discount only makes sense once the tier is actually complete — showing "save ₹123"
        // against two of three shades would misprice the bundle.
        const isComplete = this.selected.length === this.required;
        const discount = isComplete ? Math.min(this.discount, total) : 0;

        this.pricesEl.hidden = false;
        this.finalEl.textContent = this.formatMoney(total - discount);

        this.compareEl.textContent = discount > 0 ? this.formatMoney(total) : '';
        this.compareEl.hidden = discount === 0;

        this.saveEl.textContent = discount > 0 ? `${this.dataset.saveLabel} ${this.formatMoney(discount)}` : '';
        this.saveEl.hidden = discount === 0;
      }

      formatMoney(cents) {
        const amount = cents / 100;
        try {
          return new Intl.NumberFormat(document.documentElement.lang || undefined, {
            style: 'currency',
            currency: this.dataset.currency || 'INR',
          }).format(amount);
        } catch (e) {
          return amount.toFixed(2);
        }
      }

      openPicker() {
        if (this.selected.length >= this.required) return;

        this.lastFocused = document.activeElement;
        this.modal.hidden = false;
        document.body.classList.add('overflow-hidden');
        document.addEventListener('keydown', this.onKeydown);

        const firstOption = this.querySelector('[data-shade-option]:not(:disabled)');
        if (firstOption) firstOption.focus();
      }

      closePicker() {
        this.modal.hidden = true;
        document.body.classList.remove('overflow-hidden');
        document.removeEventListener('keydown', this.onKeydown);

        // Focus goes back to whatever opened the modal, unless that button is now disabled
        // because the bundle just filled up.
        if (this.lastFocused && !this.lastFocused.disabled) {
          this.lastFocused.focus();
        } else {
          this.addButton.focus();
        }
      }

      onKeydown(event) {
        if (event.key === 'Escape') {
          this.closePicker();
          return;
        }
        if (event.key !== 'Tab') return;

        // Keeps tabbing inside the dialog — without this, focus walks off into the page behind
        // it while the modal is still covering everything.
        const focusable = Array.from(this.modal.querySelectorAll('button:not([disabled])'));
        if (!focusable.length) return;

        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        const active = document.activeElement;
        const outside = !this.modal.contains(active);

        if (event.shiftKey && (active === first || outside)) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && (active === last || outside)) {
          event.preventDefault();
          first.focus();
        }
      }

      async addBundleToCart() {
        if (this.submitting || this.selected.length !== this.required) return;

        this.submitting = true;
        this.addButton.classList.add('loading');
        this.addButton.disabled = true;
        this.clearError();

        try {
          const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
            method: 'POST',
            // Same header the theme's own add-to-cart sends, so this reads as an AJAX cart add
            // to anything watching cart traffic rather than a form post.
            headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            credentials: 'same-origin',
            // Repeats of a shade are collapsed into one line with a quantity rather than
            // posted as duplicate lines.
            body: JSON.stringify({
              items: Array.from(
                (this.fixedEl ? [...this.selected, this.readShade(this.fixedEl)] : this.selected).reduce(
                  (counts, shade) => counts.set(shade.variantId, (counts.get(shade.variantId) || 0) + 1),
                  new Map()
                ),
                ([variantId, quantity]) => ({
                  id: variantId,
                  quantity: quantity,
                  // Marks the lines as one bundle so the cart/checkout discount can find them
                  // later. The leading underscore keeps it hidden from the customer.
                  properties: { _bundle: `Bundle of ${this.required}` },
                })
              ),
            }),
          });

          // A failed add doesn't always come back as JSON (a rate limit or an edge error can
          // return HTML), so don't let parsing it be what decides the message.
          const data = await response.json().catch(() => null);
          if (!response.ok) {
            throw new Error(data?.description || data?.message || 'Could not add bundle to cart');
          }

          this.openCart();
        } catch (error) {
          this.showError(error.message);
        } finally {
          // Nothing navigates away any more, so the button has to be handed back either way.
          this.submitting = false;
          this.addButton.classList.remove('loading');
          this.render();
        }
      }

      // Shopflo's bridge owns the cart. handleFloCartBtn is the global it exposes for opening
      // its drawer — the same family of hooks as the handleFloBuyNowBtn already wired into
      // snippets/buy-buttons.liquid. Only if that isn't there (bridge blocked or still loading)
      // do we fall back to Shopify's own cart page, so the shopper always ends up somewhere.
      openCart() {
        try {
          if (typeof window.handleFloCartBtn === 'function') {
            window.handleFloCartBtn();
            return;
          }
        } catch (e) {
          // Fall through to the native cart rather than stranding the shopper on the page.
        }

        window.location.href = `${window.Shopify?.routes?.root || '/'}cart`;
      }

      showError(message) {
        this.errorEl.textContent = message;
        this.errorEl.hidden = false;
      }

      clearError() {
        this.errorEl.textContent = '';
        this.errorEl.hidden = true;
      }
    }
  );
}
